import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { errorService } from "@ascleo/client/services/error.service";
import { useSchedulerExitToStart, useSchedulerSend } from "../context";
import { useAudio } from "../../../../../lib/use-audio";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { reportOperationalError } from "../../../../../lib/operational-error-telemetry";
import { KioskStep } from "../../../../../components/kiosk-step";
import {
  useCardReaderSession,
  type CardReaderReadContext,
} from "../../use-card-reader-session";
import {
  mapInsuranceType,
  resolvedCardPatientId,
  type IdentificationMode,
} from "./patient-identification-model";
import {
  useSchedulerCreatePatientForm,
  type CreatePatientStatus,
} from "./patient-identification-forms";
import { SchedulerCardModePanel } from "./scheduler-card-mode-panel";
import { SchedulerCreateModePanel } from "./scheduler-create-mode-panel";
import {
  SchedulerReceptionHandoff,
  SchedulerRestrictedSearchResultsPanel,
  SchedulerSearchResultsPanel,
} from "./scheduler-search-results-panel";
import {
  SchedulerModeTabs,
  SchedulerRestrictedModeTabs,
} from "./scheduler-mode-tabs";
import { useKioskPolicy } from "../../../../../lib/kiosk-policy/kiosk-policy-context";
import { usePatientLookup } from "../identification/patient-lookup";
import {
  KioskPatientLookupPanel,
  type KioskPatientLookupTestIds,
} from "../identification/KioskPatientLookupPanel";

const SCHEDULER_LOOKUP_TEST_IDS: KioskPatientLookupTestIds = {
  form: "frontdesk-scheduler-search-form",
  firstName: "frontdesk-scheduler-search-firstname-input",
  lastName: "frontdesk-scheduler-search-lastname-input",
  dateOfBirth: "frontdesk-scheduler-search-dob-input",
  dateOfBirthPicker: "frontdesk-scheduler-search-dob-picker-btn",
  search: "frontdesk-scheduler-search-submit-btn",
  confirm: "scheduler-confirm-patient",
  found: "frontdesk-scheduler-search-found",
  ambiguous: "frontdesk-scheduler-search-ambiguous",
  notFound: "frontdesk-scheduler-search-not-found",
  error: "frontdesk-scheduler-search-error",
};

/**
 * Patient identification step for the scheduler flow: the shared kiosk
 * identification (card, name search with the shared lookup, first visit) in
 * one fixed-width column (KO-07, KO-69). Card and search outcomes are shown
 * inline only; nothing is toasted (KO-01, KO-02).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <PatientIdentificationStep />
 * ```
 */
export function PatientIdentificationStep() {
  const { t } = useTranslation();
  const send = useSchedulerSend();
  const exitToStart = useSchedulerExitToStart();
  const kioskPolicy = useKioskPolicy();
  const [mode, setMode] = useState<IdentificationMode>("card");
  const [createStatus, setCreateStatus] = useState<CreatePatientStatus>("idle");
  const [creationRestricted, setCreationRestricted] = useState(false);

  useAudio("scheduler-patientIdentification");

  const handleReadCard = useCallback(
    async ({ readCard, setPhase, setErrorMessage }: CardReaderReadContext) => {
      const result = await readCard();
      if (result.status === "MERGED") {
        await docCirrusRpcClient.frontdesk.abandonCardRead({ crlogId: result.crlogId });
        setErrorMessage(t("self-service.check-in.card-check.read-error"));
        setPhase("error");
        return;
      }
      const insurance = mapInsuranceType(
        result.cardData.insurance?.insuranceType,
      );
      if (!insurance) {
        void reportOperationalError({
          action: "scheduler.card-insurance-unrecognized",
          severity: "error",
          source: "invariant",
          component: "PatientIdentificationStep",
          error: new Error(
            "Card response contained no supported insurance type",
          ),
          context: {
            insuranceRecognized: false,
          },
        });
        errorService.report(
          new Error("Card response contained no supported insurance type"),
          {
            layer: "rpc",
            source: "frontdesk.scheduler.card-read",
            suppressToast: true,
          },
        );
        setErrorMessage(t("self-service.check-in.card-check.read-error"));
        setPhase("card-detected");
        return;
      }

      switch (result.status) {
        case "APPLIED":
          {
            const patientId = resolvedCardPatientId(result);
            if (!patientId) {
              frontdeskDiagnosticLogger.error(
                "APPLIED/MERGED status but no patientId in response",
                {
                  status: result.status,
                },
              );
              errorService.report(
                new Error("Card read completed without a patientId"),
                {
                  layer: "rpc",
                  source: "frontdesk.scheduler.card-read",
                  suppressToast: true,
                },
              );
              setErrorMessage(t("self-service.check-in.card-check.read-error"));
              setPhase("card-detected");
              return;
            }
            send({
              type: "PATIENT_IDENTIFIED",
              patient: {
                id: patientId,
                firstName: result.cardData.givenName,
                lastName: result.cardData.familyName,
                dateOfBirth: result.cardData.birthDate,
                kvnr: result.cardData.insurance?.insuranceId,
                insurance,
              },
            });
            break;
          }
        case "MATCHING":
          await docCirrusRpcClient.frontdesk.abandonCardRead({ crlogId: result.crlogId });
          setErrorMessage(
            result.matchedPatients && result.matchedPatients.length > 0
              ? t("self-service.identification.card.not-unique")
              : t("self-service.identification.card.no-match"),
          );
          setPhase("card-detected");
          break;
        case "CANCELLED":
          setErrorMessage(t("self-service.check-in.card-check.error"));
          setPhase("card-detected");
          break;
      }
    },
    [send, t],
  );

  const handleReadCardError = useCallback(
    (error: unknown, { terminalId }: { terminalId: string | null }) => {
      frontdeskDiagnosticLogger.error("Error reading card", {
        error,
        hasTerminalId: Boolean(terminalId),
      });
      errorService.report(error, {
        layer: "rpc",
        source: "frontdesk.scheduler.card-read",
        suppressToast: true,
      });
    },
    [],
  );

  const handlePollError = useCallback(
    (error: unknown, { terminalId }: { terminalId: string | null }) => {
      frontdeskDiagnosticLogger.error("Error checking for cards", {
        error,
        hasTerminalId: Boolean(terminalId),
      });
    },
    [],
  );

  const cardReader = useCardReaderSession({
    enabled: mode === "card",
    settingsErrorMessage: t("self-service.check-in.card-check.settings-error"),
    unreadableCardMessage: t("self-service.identification.card.help-with-search"),
    readErrorMessage: t("self-service.check-in.card-check.read-error"),
    onReadCard: handleReadCard,
    onReadError: handleReadCardError,
    onPollError: handlePollError,
  });

  const lookup = usePatientLookup({ component: "SchedulerPatientIdentification" });
  const createForm = useSchedulerCreatePatientForm({
    send,
    setStatus: setCreateStatus,
    onPolicyRestricted: () => setCreationRestricted(true),
  });

  const creationAllowed =
    kioskPolicy.source === "current" &&
    kioskPolicy.patientCreationEnabled;
  const showReceptionHandoff =
    creationRestricted || (!creationAllowed && mode === "create");

  if (showReceptionHandoff) {
    return (
      <KioskStep
        width="narrow"
        align="center"
        title={t("self-service.help-reception")}
      >
        <SchedulerReceptionHandoff onHome={exitToStart} />
      </KioskStep>
    );
  }

  return (
    <KioskStep
      width="narrow"
      title={t("self-service.identification.title")}
      lead={t("self-service.identification.lead")}
    >
      {creationAllowed ? (
        <SchedulerModeTabs activeMode={mode} onModeChange={setMode} />
      ) : (
        <SchedulerRestrictedModeTabs activeMode={mode} onModeChange={setMode} />
      )}

      {mode === "card" && (
        <SchedulerCardModePanel
          cardReader={cardReader}
          searchAction={{
            label: t("self-service.identification.card.without-card"),
            icon: Search,
            onClick: () => setMode("search"),
            testId: "scheduler-card-search-btn",
          }}
        />
      )}
      {mode === "search" && (
        <KioskPatientLookupPanel
          lookup={lookup}
          idPrefix="scheduler-lookup"
          testIds={SCHEDULER_LOOKUP_TEST_IDS}
          onConfirm={({ patientId, insurance, lastName, dateOfBirth }) =>
            send({
              type: "PATIENT_IDENTIFIED",
              patient: { id: patientId, firstName: "", lastName, dateOfBirth, insurance },
            })
          }
          notFound={
            creationAllowed ? (
              <SchedulerSearchResultsPanel onCreateRequested={() => setMode("create")} />
            ) : (
              <SchedulerRestrictedSearchResultsPanel onHome={exitToStart} />
            )
          }
        />
      )}
      {creationAllowed && mode === "create" && (
        <SchedulerCreateModePanel form={createForm} status={createStatus} />
      )}
    </KioskStep>
  );
}
